<template>
    <div class="log-container">
        <!-- 面包屑导航区域 -->
        <el-breadcrumb separator="/" class="breadcrumb">
            <el-breadcrumb-item :to="{ path: '/index' }">首页</el-breadcrumb-item>
            <el-breadcrumb-item>系统管理</el-breadcrumb-item>
            <el-breadcrumb-item>操作日志</el-breadcrumb-item>
        </el-breadcrumb>

        <!-- 主要内容区域 -->
        <el-card class="main-card" shadow="hover">
            <!-- 搜索筛选区域 -->
            <div class="search-section">
                <el-row :gutter="10">
                    <el-col :span="3">
                        <el-input
                            placeholder="操作地址"
                            v-model="queryInfo.operIp"
                            clearable
                            @clear="getList()"
                            @keyup.enter.native="getList()"
                            prefix-icon="el-icon-location">
                        </el-input>
                    </el-col>
                    <el-col :span="3">
                        <el-input
                            placeholder="系统模块"
                            v-model="queryInfo.module"
                            clearable
                            @clear="getList()"
                            @keyup.enter.native="getList()"
                            prefix-icon="el-icon-menu">
                        </el-input>
                    </el-col>
                    <el-col :span="3">
                        <el-input
                            placeholder="操作人员"
                            v-model="queryInfo.query"
                            clearable
                            @clear="getList()"
                            @keyup.enter.native="getList()"
                            prefix-icon="el-icon-user">
                        </el-input>
                    </el-col>
                    <el-col :span="2">
                        <div class="select-prefix-group">
                            <span class="select-prefix-label">类型：</span>
                            <el-select
                                v-model="queryInfo.operate"
                                placeholder="选择"
                                clearable
                                @change="getList()"
                                style="width: 100%">
                                <el-option label="全部" value=""></el-option>
                                <el-option
                                    v-for="t in logTypes"
                                    :key="t.code"
                                    :label="t.label"
                                    :value="t.label">
                                </el-option>
                            </el-select>
                        </div>
                    </el-col>
                    <el-col :span="3">
                        <el-input
                            placeholder="操作描述"
                            v-model="queryInfo.content"
                            clearable
                            @clear="getList()"
                            @keyup.enter.native="getList()"
                            prefix-icon="el-icon-document">
                        </el-input>
                    </el-col>
                    <el-col :span="3">
                        <el-date-picker
                            v-model="dateRange"
                            type="daterange"
                            range-separator="至"
                            start-placeholder="开始日期"
                            end-placeholder="结束日期"
                            format="yyyy-MM-dd"
                            value-format="yyyy-MM-dd"
                            @change="onDateRangeChange"
                            style="width: 100%">
                        </el-date-picker>
                    </el-col>
                    <el-col :span="2">
                        <div class="select-prefix-group">
                            <span class="select-prefix-label">状态：</span>
                            <el-select
                                v-model="queryInfo.status"
                                placeholder="选择"
                                clearable
                                @change="getList()"
                                style="width: 100%">
                                <el-option label="全部" value=""></el-option>
                                <el-option label="正常" :value="0"></el-option>
                                <el-option label="异常" :value="1"></el-option>
                            </el-select>
                        </div>
                    </el-col>
                    <el-col :span="5" style="text-align: right;">
                        <el-button type="primary" icon="el-icon-search" @click="getList()">搜索</el-button>
                    </el-col>
                </el-row>
            </div>

            <!-- 统计信息 -->
            <div class="stats-section" v-if="total > 0">
                <el-alert
                    :title="`共找到 ${total} 条操作记录`"
                    type="info"
                    :closable="false"
                    show-icon>
                </el-alert>
            </div>

            <!-- 日志列表区域 -->
            <div class="table-section">
                <el-table
                    :data="list"
                    v-loading="loading"
                    style="width: 100%"
                    stripe
                    size="medium"
                    :header-cell-style="{background: '#f5f7fa', color: '#606266', fontWeight: 'bold'}"
                    @row-click="handleRowClick">

                    <el-table-column type="selection" width="55" align="center"></el-table-column>
                    <el-table-column label="序号" type="index" width="60" align="center"></el-table-column>

                    <el-table-column label="系统模块" prop="module" width="120" align="center" show-overflow-tooltip>
                        <template slot-scope="scope">
                            <span>{{ scope.row.module || '-' }}</span>
                        </template>
                    </el-table-column>

                    <el-table-column label="操作类型" prop="operate" width="120" align="center">
                        <template slot-scope="scope">
                            <el-tag
                                size="small"
                                :type="getOperateType(scope.row.operate)">
                                {{ scope.row.operate || '-' }}
                            </el-tag>
                        </template>
                    </el-table-column>

                    <el-table-column label="操作人员" prop="username" width="120" align="center">
                        <template slot-scope="scope">
                            <span>{{ scope.row.username || '-' }}</span>
                        </template>
                    </el-table-column>

                    <el-table-column label="角色名称" prop="roleName" width="120" align="center">
                        <template slot-scope="scope">
                            <span>{{ scope.row.roleName || '-' }}</span>
                        </template>
                    </el-table-column>

                    <el-table-column label="操作地址" prop="operIp" width="150" align="center" show-overflow-tooltip>
                        <template slot-scope="scope">
                            <span>{{ scope.row.operIp || '-' }}</span>
                        </template>
                    </el-table-column>

                    <el-table-column label="操作地点" prop="loginLocation" width="130" align="center" show-overflow-tooltip>
                        <template slot-scope="scope">
                            <span>{{ scope.row.loginLocation || '-' }}</span>
                        </template>
                    </el-table-column>

                    <el-table-column label="操作方法" prop="method" min-width="200" show-overflow-tooltip>
                        <template slot-scope="scope">
                            <span>{{ scope.row.method || '-' }}</span>
                        </template>
                    </el-table-column>

                    <el-table-column label="操作描述" prop="content" min-width="180" show-overflow-tooltip>
                        <template slot-scope="scope">
                            <span>{{ scope.row.content || '-' }}</span>
                        </template>
                    </el-table-column>

                    <el-table-column label="操作状态" prop="status" width="90" align="center">
                        <template slot-scope="scope">
                            <el-tag size="small" :type="getStatusType(scope.row.status)">
                                {{ getStatusText(scope.row.status) }}
                            </el-tag>
                        </template>
                    </el-table-column>

                    <el-table-column label="操作时间" prop="adddate" width="170" align="center">
                        <template slot-scope="scope">
                            <div class="time-cell">
                                <i class="el-icon-time"></i>
                                <span>{{ formatTime(scope.row.adddate) }}</span>
                            </div>
                        </template>
                    </el-table-column>

                    <el-table-column label="耗时" prop="costTime" width="80" align="center">
                        <template slot-scope="scope">
                            <span v-if="scope.row.costTime">{{ scope.row.costTime }}ms</span>
                            <span v-else>-</span>
                        </template>
                    </el-table-column>

                    <el-table-column label="操作" width="80" align="center" fixed="right">
                        <template slot-scope="scope">
                            <el-button
                                type="text"
                                size="small"
                                @click.stop="showLogDetail(scope.row)"
                                title="查看详情">
                                <i class="el-icon-view"></i> 详情
                            </el-button>
                        </template>
                    </el-table-column>
                </el-table>
            </div>

            <!-- 分页区域 -->
            <div class="pagination-section">
                <el-pagination
                    @size-change="handleSizeChange"
                    @current-change="handleCurrentChange"
                    :current-page="queryInfo.pagenum"
                    :page-sizes="[20, 50, 100, 200]"
                    :page-size="queryInfo.pagesize"
                    layout="total, sizes, prev, pager, next, jumper"
                    :total="total"
                    background>
                </el-pagination>
            </div>
        </el-card>

        <!-- 操作日志详情对话框 -->
        <el-dialog
            title="操作日志详情"
            :visible.sync="logDetailDialogVisible"
            width="800px"
            :before-close="closeLogDetailDialog"
            class="log-detail-dialog">
            <div class="log-detail scroll-body" v-if="currentLogDetail">
                <!-- 基本信息 -->
                <div class="detail-section">
                    <div class="section-title">
                        <i class="el-icon-info"></i>
                        <span>基本信息</span>
                    </div>
                    <div class="section-body">
                        <div class="detail-row">
                            <div class="detail-item">
                                <span class="detail-label">操作模块</span>
                                <span class="detail-value">{{ currentLogDetail.module || '-' }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">业务类型</span>
                                <span class="detail-value">{{ currentLogDetail.businessType || currentLogDetail.operate || '-' }}</span>
                            </div>
                        </div>
                        <div class="detail-row">
                            <div class="detail-item">
                                <span class="detail-label">操作时间</span>
                                <span class="detail-value">{{ formatTime(currentLogDetail.adddate) }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">执行状态</span>
                                <span class="detail-value">
                                    <el-tag size="small" :type="getStatusType(currentLogDetail.status)">
                                        <i :class="currentLogDetail.status === 0 ? 'el-icon-circle-check' : 'el-icon-circle-close'"></i>
                                        {{ getStatusText(currentLogDetail.status) }}
                                    </el-tag>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 操作人员 -->
                <div class="detail-section">
                    <div class="section-title">
                        <i class="el-icon-user"></i>
                        <span>操作人员</span>
                    </div>
                    <div class="section-body">
                        <div class="detail-row">
                            <div class="detail-item">
                                <span class="detail-label">操作人员</span>
                                <span class="detail-value">{{ currentLogDetail.username || '-' }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">所属部门</span>
                                <span class="detail-value">{{ currentLogDetail.roleName || '-' }}</span>
                            </div>
                        </div>
                        <div class="detail-row">
                            <div class="detail-item">
                                <span class="detail-label">操作地址</span>
                                <span class="detail-value">{{ currentLogDetail.operIp || '-' }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">登录地点</span>
                                <span class="detail-value">{{ currentLogDetail.loginLocation || '-' }}</span>
                            </div>
                        </div>
                        <div class="detail-row">
                            <div class="detail-item">
                                <span class="detail-label">浏览器</span>
                                <span class="detail-value">{{ currentLogDetail.browser || '-' }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">操作系统</span>
                                <span class="detail-value">{{ currentLogDetail.os || '-' }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 请求信息 -->
                <div class="detail-section">
                    <div class="section-title">
                        <i class="el-icon-link"></i>
                        <span>请求信息</span>
                    </div>
                    <div class="section-body">
                        <div class="detail-row">
                            <div class="detail-item full">
                                <span class="detail-label">请求地址</span>
                                <span class="detail-value">
                                    <el-tag size="mini" :type="getMethodTagType(currentLogDetail.requestMethod)">{{ currentLogDetail.requestMethod || 'GET' }}</el-tag>
                                    <span style="margin-left: 8px;">{{ currentLogDetail.operUrl || currentLogDetail.operIp || '-' }}</span>
                                </span>
                            </div>
                        </div>
                        <div class="detail-row">
                            <div class="detail-item full">
                                <span class="detail-label">操作方法</span>
                                <span class="detail-value method-text">{{ currentLogDetail.method || '-' }}</span>
                            </div>
                        </div>
                        <div class="detail-row">
                            <div class="detail-item full">
                                <span class="detail-label">消耗时间</span>
                                <span class="detail-value">{{ currentLogDetail.costTime != null ? currentLogDetail.costTime + ' 毫秒' : '-' }}</span>
                            </div>
                        </div>
                        <div class="detail-row" v-if="currentLogDetail.status === 1 && currentLogDetail.errorMsg">
                            <div class="detail-item full">
                                <span class="detail-label">错误信息</span>
                                <span class="detail-value error-text">{{ currentLogDetail.errorMsg }}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 请求参数 -->
                <div class="detail-section">
                    <div class="section-title">
                        <i class="el-icon-coin"></i>
                        <span>请求参数</span>
                    </div>
                    <div class="section-body">
                        <div class="code-block">
                            <pre>{{ formatParams(currentLogDetail.params) }}</pre>
                            <el-button
                                v-if="currentLogDetail.params && currentLogDetail.params !== 'null'"
                                type="text"
                                size="mini"
                                class="copy-btn"
                                @click="copyText(formatParams(currentLogDetail.params))">
                                <i class="el-icon-document-copy"></i> 复制
                            </el-button>
                        </div>
                    </div>
                </div>

                <!-- 返回参数 -->
                <div class="detail-section">
                    <div class="section-title">
                        <i class="el-icon-refresh"></i>
                        <span>返回参数</span>
                    </div>
                    <div class="section-body">
                        <div class="code-block">
                            <pre>{{ currentLogDetail.responseData ? formatParams(currentLogDetail.responseData) : '（无返回数据）' }}</pre>
                            <el-button
                                v-if="currentLogDetail.responseData && currentLogDetail.responseData !== 'null'"
                                type="text"
                                size="mini"
                                class="copy-btn"
                                @click="copyText(formatParams(currentLogDetail.responseData))">
                                <i class="el-icon-document-copy"></i> 复制
                            </el-button>
                        </div>
                    </div>
                </div>
            </div>
            <span slot="footer" class="dialog-footer">
                <el-button @click="closeLogDetailDialog">关闭</el-button>
            </span>
        </el-dialog>
    </div>
</template>
<script>
export default {
    data() {
        return {
            queryInfo: {
                query: '',
                operate: '',
                module: '',
                businessType: '',
                status: '',
                operIp: '',
                content: '',
                startDate: '',
                endDate: '',
                pagenum: 1,
                pagesize: 20
            },
            total: 0,
            list: [],
            loading: false,
            dateRange: [],
            logDetailDialogVisible: false,
            currentLogDetail: null,
            logTypes: []

        }
    },

    created() {
        this.getList()
        this.fetchLogTypes()
    },

    methods: {
        async fetchLogTypes() {
            try {
                const { data: res } = await this.$http.get('logtypes/');
                if (res.status == 1 && res.data) {
                    this.logTypes = res.data;
                }
            } catch (error) {
                console.error('获取日志类型列表失败:', error);
            }
        },

        async getList() {
            this.loading = true;
            try {
                const { data: res } = await this.$http.get('loglist/', { params: this.queryInfo });
                if (res.status == 1) {
                    this.total = res.data.totalCount;
                    this.list = res.data.list.map(v => {
                        const item = v.fields;
                        item.id = v.pk;
                        return item;
                    });
                } else {
                    this.$message.error('获取日志列表失败');
                }
            } catch (error) {
                console.error('获取日志列表错误:', error);
                this.$message.error('获取日志列表失败');
            } finally {
                this.loading = false;
            }
        },

        handleSizeChange(newSize) {
            this.queryInfo.pagesize = newSize;
            this.queryInfo.pagenum = 1;
            this.getList();
        },

        handleCurrentChange(newPage) {
            this.queryInfo.pagenum = newPage;
            this.getList();
        },

        onDateRangeChange(dateRange) {
            if (dateRange && dateRange.length === 2) {
                this.queryInfo.startDate = dateRange[0] + ' 00:00:00';
                this.queryInfo.endDate = dateRange[1] + ' 23:59:59';
            } else {
                this.queryInfo.startDate = '';
                this.queryInfo.endDate = '';
            }
        },

        resetSearch() {
            this.queryInfo = {
                query: '',
                operate: '',
                module: '',
                businessType: '',
                status: '',
                operIp: '',
                content: '',
                startDate: '',
                endDate: '',
                pagenum: 1,
                pagesize: 20
            };
            this.dateRange = [];
            this.getList();
        },

        getOperateType(operate) {
            const typeMap = {
                '登录': 'success',
                '退出登录': 'info',
                '新增': 'primary',
                '修改': 'warning',
                '删除': 'danger',
                '查询': 'info',
                '导入': 'primary',
                '导出': 'success',
                '审核': 'warning',
                '回复': 'primary',
                '上传': 'success',
                '锁定': 'danger',
                '解锁': 'success',
                '批量操作': 'warning',
                '标记': 'info',
                '回调': 'warning',
                '其他': 'info'
            };
            return typeMap[operate] || '';
        },

        getStatusType(status) {
            // 0=正常/成功，1=异常/失败
            if (status === 0 || status === '0') return 'success';
            if (status === 1 || status === '1') return 'danger';
            return 'info';
        },

        getStatusText(status) {
            if (status === 0 || status === '0') return '正常';
            if (status === 1 || status === '1') return '异常';
            return '未知';
        },

        getMethodTagType(method) {
            const map = {
                'GET': 'success',
                'POST': 'primary',
                'PUT': 'warning',
                'DELETE': 'danger',
                'PATCH': 'info'
            };
            return map[method] || 'info';
        },

        formatTime(timeStr) {
            if (!timeStr) return '-';
            try {
                if (/^\d+$/.test(timeStr)) {
                    const date = new Date(parseInt(timeStr) * 1000);
                    return date.toLocaleString('zh-CN');
                }
                return timeStr;
            } catch (error) {
                return timeStr;
            }
        },

        formatParams(params) {
            if (!params || params === 'null' || params === 'undefined') {
                return '（无数据）';
            }
            try {
                const parsed = JSON.parse(params);
                return JSON.stringify(parsed, null, 2);
            } catch (error) {
                return params;
            }
        },

        showLogDetail(row) {
            this.currentLogDetail = row;
            this.logDetailDialogVisible = true;
        },

        closeLogDetailDialog() {
            this.logDetailDialogVisible = false;
            this.currentLogDetail = null;
        },

        handleRowClick(row) {
            this.showLogDetail(row);
        },

        copyText(text) {
            if (text) {
                navigator.clipboard.writeText(text).then(() => {
                    this.$message.success('已复制到剪贴板');
                }).catch(() => {
                    this.$message.error('复制失败');
                });
            }
        }
    }
}
</script>
<style scoped>
.log-container {
    /* 不加 padding：外层 el-main 已有 20px 内边距，再加会双重缩进导致面包屑比其他页面靠右 */
    background: #f5f7fa;
    min-height: 100vh;
}

.breadcrumb {
    /* 覆盖全局 style.css 的 .el-breadcrumb { width: 90%; margin: 10px auto }——
       那条规则会把面包屑白条缩成 90% 居中，左侧空出 5% 宽白边，与下方卡片左缘对不上 */
    width: 100%;
    margin: 0 0 20px;
    background: white;
    padding: 12px 16px;
    border-radius: 6px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.main-card {
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    border: none;
}

.search-section {
    padding: 20px;
    background: #fafbfc;
    border-radius: 6px;
    margin-bottom: 20px;
    border: 1px solid #e4e7ed;
}

.stats-section {
    margin-bottom: 20px;
}

.table-section {
    margin-bottom: 20px;
}

.pagination-section {
    display: flex;
    justify-content: center;
    padding: 20px 0;
    background: #fafbfc;
    border-radius: 6px;
    border: 1px solid #e4e7ed;
}

.time-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 13px;
}

.time-cell i {
    color: #909399;
}

/* 表格样式 */
.el-table tbody tr:hover {
    cursor: pointer;
    background-color: #f5f7fa !important;
}

.el-table th {
    font-weight: 600;
    font-size: 14px;
}

.el-table {
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid #ebeef5;
}

.el-table::before {
    height: 0;
}

/* 详情对话框样式 */
.log-detail-dialog >>> .el-dialog__header {
    background: #f5f5f5;
    border-bottom: 1px solid #e4e7ed;
    padding: 15px 20px;
}

.log-detail-dialog >>> .el-dialog__title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
}

.log-detail-dialog >>> .el-dialog__body {
    padding: 15px 20px;
    background: #f5f7fa;
}

.log-detail-dialog >>> .el-dialog__footer {
    border-top: 1px solid #e4e7ed;
    padding: 15px 20px;
}

.log-detail {
    padding: 0;
}

.detail-section {
    background: #fff;
    border-radius: 8px;
    margin-bottom: 15px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.detail-section:last-child {
    margin-bottom: 0;
}

.section-title {
    background: #f8f9fa;
    padding: 12px 16px;
    font-size: 14px;
    font-weight: 600;
    color: #303133;
    border-bottom: 1px solid #ebeef5;
    display: flex;
    align-items: center;
    gap: 8px;
}

.section-title i {
    color: #909399;
    font-size: 16px;
}

.section-body {
    padding: 12px 16px;
}

.detail-row {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 10px;
}

.detail-row:last-child {
    margin-bottom: 0;
}

.detail-item {
    flex: 1;
    min-width: 200px;
    display: flex;
    align-items: center;
    padding: 6px 0;
}

.detail-item.full {
    flex: 0 0 100%;
}

.detail-label {
    color: #909399;
    font-size: 13px;
    width: 80px;
    flex-shrink: 0;
    text-align: right;
    padding-right: 12px;
}

.detail-value {
    color: #303133;
    font-size: 13px;
    flex: 1;
    word-break: break-all;
}

.method-text {
    font-family: 'Courier New', monospace;
    color: #606266;
    background: #f5f7fa;
    padding: 2px 6px;
    border-radius: 3px;
}

.error-text {
    color: #f56c6c;
    background: #fef0f0;
    padding: 4px 8px;
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    font-size: 12px;
}

/* 弹窗内容固定高度滚动 */
.scroll-body {
    max-height: 55vh;
    overflow-y: auto;
    padding-right: 5px;
}

.scroll-body::-webkit-scrollbar {
    width: 6px;
}

.scroll-body::-webkit-scrollbar-thumb {
    background: #dcdfe6;
    border-radius: 3px;
}

.scroll-body::-webkit-scrollbar-thumb:hover {
    background: #c0c4cc;
}

/* 代码块样式 */
.code-block {
    position: relative;
    background: #f8f9fa;
    border: 1px solid #e4e7ed;
    border-radius: 6px;
    padding: 12px;
    min-height: 60px;
    max-height: 200px;
    overflow-y: auto;
}

.code-block pre {
    margin: 0;
    font-family: 'Courier New', monospace;
    font-size: 12px;
    color: #67c23a;
    white-space: pre-wrap;
    word-break: break-all;
    line-height: 1.6;
}

.code-block::-webkit-scrollbar {
    width: 5px;
}

.code-block::-webkit-scrollbar-thumb {
    background: #dcdfe6;
    border-radius: 3px;
}

.code-block .copy-btn {
    position: absolute;
    top: 8px;
    right: 8px;
    color: #909399;
}

.code-block .copy-btn:hover {
    color: #409eff;
}

/* 搜索区域 */
.search-section .el-input__inner,
.search-section .el-select .el-input__inner {
    border-radius: 4px;
}

.select-prefix-group {
    display: flex;
    align-items: center;
    height: 40px;
}

.select-prefix-label {
    flex-shrink: 0;
    font-size: 14px;
    color: #909399;
    margin-right: 4px;
    white-space: nowrap;
}

/* 分页 */
.el-pagination {
    font-weight: 500;
}

/* 加载 */
.el-loading-mask {
    border-radius: 6px;
}

/* 响应式 */
@media (max-width: 768px) {
    .log-container {
        padding: 10px;
    }

    .search-section {
        padding: 15px;
    }

    .el-col {
        margin-bottom: 10px;
    }
}
</style>
